import React, { useState } from 'react';
import { Link } from '@/components/LocaleLink';
import { cn } from '@/lib/utils';
import SEO from '@/components/SEO';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import useScrollReveal from '@/hooks/useScrollReveal';
import { useLanguage } from '@/contexts/language/LanguageContext';
import { dataWarehousingCopy } from '@/contexts/language/translations/pages/dataWarehousing';
import { stairwayCopy } from '@/contexts/language/translations/pages/stairway';

/* ── Cloud platforms ── */
const PLATFORMS = [
  {
    id: 'azure',
    name: 'Microsoft Azure',
    logo: '/lovable-uploads/azure_icon.png',
    color: '#0078D4',
    bg: '#EFF6FF',
  },
  {
    id: 'fabric',
    name: 'Microsoft Fabric',
    logo: '/lovable-uploads/powerbi_icon.png',
    color: '#7B2FBE',
    bg: '#F5F0FF',
  },
  {
    id: 'gcp',
    name: 'Google Cloud Platform',
    logo: '/lovable-uploads/bigq-black-removebg-preview.png',
    color: '#34A853',
    bg: '#F0FDF4',
  },
  {
    id: 'aws',
    name: 'Amazon Web Services',
    logo: '/lovable-uploads/aws_icon.png',
    color: '#FF9900',
    bg: '#FFFBEB',
  },
];

/* ── Core capabilities ── */
const CAPABILITIES = [
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 2.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125m16.5 2.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125" />
      </svg>
    ),
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
      </svg>
    ),
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5m.75-9l3-3 2.148 2.148A12.061 12.061 0 0116.5 7.605" />
      </svg>
    ),
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
      </svg>
    ),
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
      </svg>
    ),
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
      </svg>
    ),
  },
];

/* ── The stairway connection ── */
const ACTIVE_STAIRWAY_LEVEL = 3;

const STEP_CHAIN_COLORS = ['#2A1E47', '#4E3D6E', '#6F5D91', '#FF6B35'];

const PHASE_COLORS = ['#2A1E47', '#3D2E5C', '#5F4D80', '#FF6B35'];

const OurWorkDataEngineering = () => {
  const [activePlatform, setActivePlatform] = useState('gcp');
  useScrollReveal();
  const { language } = useLanguage();
  const c = dataWarehousingCopy[language];
  const stairway = stairwayCopy[language];

  const STAIRWAY_STEPS = stairway.stepNamesShort.map((label, i) => ({
    level: i + 1,
    label,
    active: i + 1 === ACTIVE_STAIRWAY_LEVEL,
  }));

  const localizedPlatforms = PLATFORMS.map((p) => ({
    ...p,
    ...c.platforms[p.id as keyof typeof c.platforms],
  }));

  const platform = localizedPlatforms.find(p => p.id === activePlatform)!;

  return (
    <div className="flex flex-col min-h-screen">
      <SEO
        title={c.seoTitle}
        description={c.seoDescription}
        canonical="/our-work/data-engineering"
      />
      <Navigation />
      <main className="index-page flex-1">

        {/* Hero */}
        <section className="relative pt-28 pb-20 md:pt-36 md:pb-28 overflow-hidden bg-white">
          <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-[#E0E7FF] rounded-full filter blur-3xl opacity-30" />
          <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-[#FFF7EC] rounded-full filter blur-3xl opacity-60" />
          <div className="container-custom relative z-10 reveal">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-[#2A1E47] mb-6 leading-[1.08]">
                {c.hero.title}
              </h1>
              <p className="text-lg md:text-xl text-[#2A1E47]/80 max-w-2xl mx-auto mb-3">
                {c.hero.subtitle}
              </p>
              <p className="text-base text-muted-foreground max-w-xl mx-auto mb-10">
                {c.hero.text}
              </p>
              <div className={`flex items-center justify-center gap-4 flex-wrap${c.hero.ctaNote ? ' sm:items-stretch' : ''}`}>
                <Link
                  to="/tell-me-more"
                  className={`inline-flex items-center justify-center rounded-lg bg-[#FF6B35] text-white font-medium hover:bg-[#FF6B35]/90 transition-colors ${c.hero.ctaNote ? 'flex-col py-3 px-6 sm:px-8' : 'px-8 py-3'}`}
                >
                  {c.hero.ctaNote ? (
                    <>
                      <span className="text-base sm:text-xl whitespace-nowrap">{c.hero.cta}</span>
                      <span className="text-[10px] sm:text-[11px] font-normal opacity-80 whitespace-nowrap">{c.hero.ctaNote}</span>
                    </>
                  ) : c.hero.cta}
                </Link>
                <Link
                  to="/our-work/data"
                  className="inline-flex items-center gap-2 text-[#2A1E47]/60 hover:text-[#2A1E47] text-sm font-medium transition-colors"
                >
                  {c.hero.stairwayLink}
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Step 3 connection */}
        <section className="py-16 bg-[#2A1E47]">
          <div className="container-custom reveal">
            <div className="max-w-4xl mx-auto">
              <div className="flex flex-col md:flex-row items-center gap-10 md:gap-16">
                {/* Mini stairway */}
                <div className="flex items-end gap-2 shrink-0">
                  {STAIRWAY_STEPS.map((step) => {
                    const h = 20 + step.level * 14;
                    return (
                      <div key={step.level} className="flex flex-col items-center gap-1">
                        <div
                          className={cn(
                            'w-8 rounded-t-md transition-all',
                            step.active ? 'ring-2 ring-[#FF6B35] ring-offset-2 ring-offset-[#2A1E47]' : ''
                          )}
                          style={{
                            height: h,
                            backgroundColor: step.active ? '#FF6B35' : 'rgba(255,255,255,0.15)',
                          }}
                        />
                        <span className={cn('text-[10px] font-bold', step.active ? 'text-[#FF6B35]' : 'text-white/30')}>
                          {step.level}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {/* Text */}
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF6B35] mb-3">{c.step3.eyebrow}</p>
                  <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
                    {c.step3.title}
                  </h2>
                  <p className="text-white/60 leading-relaxed">
                    {c.step3.text.split('**').map((part, i) =>
                      i % 2 ? <span key={i} className="text-white">{part}</span> : part
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Cross-platform expertise */}
        <section className="py-20 md:py-28 bg-white">
          <div className="container-custom reveal">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-[#2A1E47] mb-3">
              {c.platformsSection.title}
            </h2>
            <p className="text-center text-muted-foreground text-lg max-w-2xl mx-auto mb-14">
              {c.platformsSection.subtitle}
            </p>

            {/* Platform tabs */}
            <div className="flex flex-wrap justify-center gap-3 mb-10">
              {PLATFORMS.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setActivePlatform(p.id)}
                  className={cn(
                    'flex items-center gap-2.5 px-5 py-3 rounded-xl border-2 font-medium text-sm transition-all duration-200',
                    activePlatform === p.id
                      ? 'border-transparent shadow-lg scale-[1.03]'
                      : 'border-gray-100 bg-white text-[#2A1E47]/60 hover:border-gray-200 hover:text-[#2A1E47]'
                  )}
                  style={activePlatform === p.id ? { backgroundColor: p.bg, color: p.color, borderColor: p.color + '40' } : {}}
                >
                  <img src={p.logo} alt={p.name} className="w-5 h-5 object-contain" />
                  {p.name}
                </button>
              ))}
            </div>

            {/* Platform detail card */}
            <div
              className="max-w-4xl mx-auto rounded-2xl border-2 p-8 md:p-10 transition-all duration-300"
              style={{ backgroundColor: platform.bg, borderColor: platform.color + '25' }}
            >
              <div className="flex flex-col md:flex-row gap-8">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <img src={platform.logo} alt={platform.name} className="w-10 h-10 object-contain" />
                    <h3 className="text-xl font-bold text-[#2A1E47]">{platform.name}</h3>
                  </div>
                  <p className="text-[#2A1E47]/70 leading-relaxed mb-6">{platform.description}</p>
                  <Link
                    to="/tell-me-more"
                    className="inline-flex items-center gap-2 text-sm font-semibold transition-colors"
                    style={{ color: platform.color }}
                  >
                    {c.platformsSection.discuss(platform.name)}
                  </Link>
                </div>
                <div className="flex-1">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] mb-4" style={{ color: platform.color }}>
                    {c.platformsSection.servicesLabel}
                  </p>
                  <div className="space-y-2">
                    {platform.services.map((service) => (
                      <div key={service} className="flex items-center gap-3">
                        <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: platform.color }} />
                        <span className="text-sm text-[#2A1E47]/80 font-medium">{service}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Platform logos strip */}
            <div className="flex items-center justify-center gap-8 md:gap-14 mt-14 flex-wrap">
              {[
                { src: '/lovable-uploads/azure_icon.png', label: 'Azure' },
                { src: '/lovable-uploads/powerbi_icon.png', label: 'Fabric' },
                { src: '/lovable-uploads/bigq-black-removebg-preview.png', label: 'BigQuery' },
                { src: '/lovable-uploads/aws_icon.png', label: 'AWS' },
                { src: '/lovable-uploads/dbt_icon.png', label: 'dbt' },
                { src: '/lovable-uploads/looker_info.png', label: 'Looker' },
              ].map((logo) => (
                <div key={logo.label} className="flex flex-col items-center gap-2 opacity-50 hover:opacity-100 transition-opacity">
                  <img src={logo.src} alt={logo.label} className="h-8 w-auto object-contain grayscale hover:grayscale-0 transition-all" />
                  <span className="text-xs text-muted-foreground">{logo.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Core capabilities */}
        <section className="py-20 md:py-28 bg-[#FAFAFA]">
          <div className="container-custom reveal">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-[#2A1E47] mb-3">
              {c.capabilitiesSection.title}
            </h2>
            <p className="text-center text-muted-foreground text-lg max-w-2xl mx-auto mb-16">
              {c.capabilitiesSection.subtitle}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {c.capabilities.map((text, i) => ({ ...text, icon: CAPABILITIES[i].icon })).map((cap) => (
                <div key={cap.title} className="rounded-2xl bg-white border border-gray-100 p-7 shadow-sm hover:shadow-md transition-shadow group">
                  <div className="w-11 h-11 rounded-xl bg-[#E0E7FF] flex items-center justify-center text-[#2A1E47]/70 mb-5 group-hover:bg-[#2A1E47] group-hover:text-white transition-colors">
                    {cap.icon}
                  </div>
                  <h3 className="text-base font-bold text-[#2A1E47] mb-2">{cap.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{cap.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* AI enablement bridge */}
        <section className="py-20 md:py-28 bg-white">
          <div className="container-custom reveal">
            <div className="max-w-5xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-center">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF6B35] mb-4">{c.ai.eyebrow}</p>
                  <h2 className="text-3xl md:text-4xl font-bold text-[#2A1E47] mb-6">
                    {c.ai.titleLine1}<br />{c.ai.titleLine2}
                  </h2>
                  <p className="text-muted-foreground leading-relaxed mb-5">
                    {c.ai.p1}
                  </p>
                  <p className="text-muted-foreground leading-relaxed mb-5">
                    {c.ai.p2}
                  </p>
                  <p className="text-muted-foreground leading-relaxed mb-8">
                    {c.ai.p3}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Link
                      to="/our-work/context-engineering"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border-2 border-[#2A1E47]/15 text-[#2A1E47] text-sm font-medium hover:border-[#2A1E47]/30 transition-colors"
                    >
                      {c.ai.contextEngineeringLink}
                    </Link>
                    <Link
                      to="/our-work/data"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border-2 border-[#FF6B35]/20 text-[#FF6B35] text-sm font-medium hover:border-[#FF6B35]/40 transition-colors"
                    >
                      {c.ai.stairwayLink}
                    </Link>
                  </div>
                </div>

                {/* Step chain visual */}
                <div className="space-y-3">
                  {c.ai.chainSubs.map((sub, i) => ({
                    step: i + 3,
                    label: i === 0 ? c.ai.step3Label : stairway.stepNames[i + 2],
                    sub,
                    highlight: i === 0,
                    color: STEP_CHAIN_COLORS[i],
                  })).map((item, i) => (
                    <div key={item.step} className="relative">
                      {i < 3 && (
                        <div className="absolute left-5 top-full h-3 w-px bg-gray-200 z-10" />
                      )}
                      <div
                        className={cn(
                          'flex items-center gap-4 rounded-xl p-4 transition-all',
                          item.highlight ? 'shadow-lg ring-2 ring-[#FF6B35]/20' : 'border border-gray-100'
                        )}
                        style={{ backgroundColor: item.highlight ? '#FFF7EC' : 'white' }}
                      >
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0"
                          style={{ backgroundColor: item.color }}
                        >
                          {item.step}
                        </div>
                        <div>
                          <p className={cn('font-bold text-sm', item.highlight ? 'text-[#FF6B35]' : 'text-[#2A1E47]')}>
                            {item.label}
                            {item.highlight && <span className="ml-2 text-[10px] bg-[#FF6B35] text-white px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">{c.ai.badge}</span>}
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">{item.sub}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How we work */}
        <section className="py-20 md:py-28 bg-[#FAFAFA]">
          <div className="container-custom reveal">
            <div className="max-w-4xl mx-auto">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF6B35] mb-4">{c.howWeWork.eyebrow}</p>
              <h2 className="text-3xl md:text-4xl font-bold text-[#2A1E47] mb-12">
                {c.howWeWork.title}
              </h2>
              <div className="space-y-0">
                {c.howWeWork.phases.map((p, i) => ({ ...p, color: PHASE_COLORS[i] })).map((phase, i) => (
                  <div key={phase.title} className="relative pl-12 md:pl-16 pb-12 last:pb-0">
                    {i < 3 && (
                      <div className="absolute left-[19px] md:left-[23px] top-10 bottom-0 w-px bg-[#2A1E47]/10" />
                    )}
                    <div
                      className="absolute left-0 top-0 w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-md"
                      style={{ backgroundColor: phase.color }}
                    >
                      {i + 1}
                    </div>
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-[#2A1E47]">{phase.title}</h3>
                        <span className="text-xs font-medium text-[#FF6B35] bg-[#FFF7EC] px-2 py-0.5 rounded-full">{phase.phase}</span>
                      </div>
                      <p className="text-muted-foreground leading-relaxed">{phase.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative overflow-hidden py-20">
          <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B35] via-[#FF7F27] to-[#FF4500] rounded-3xl" />
          <div className="container-custom relative z-10">
            <div className="max-w-3xl mx-auto text-center pt-16 pb-8 px-4 reveal">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 text-white leading-tight">
                {c.cta.title}
              </h2>
              <p className="text-lg md:text-xl text-white/90 mb-10 max-w-xl mx-auto">
                {c.cta.text}
              </p>
              <Link
                to="/tell-me-more"
                className="inline-flex items-center justify-center px-8 py-4 rounded-lg bg-[#2a1e48] hover:bg-[#2a1e48]/90 text-white text-base font-medium transition-all duration-300 shadow-lg border border-white/10"
              >
                {c.cta.button}
              </Link>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
};

export default OurWorkDataEngineering;
